import { textValue } from '../evidence.js';
import { CART_SOURCE, type CartPartial } from './cart-partial.js';
import {
  normalizeCurrency,
  parseItemQuantity,
  parseMoney,
} from './parse-money.js';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

type Entry = Record<string, unknown>;

const CART_EVENTS = new Set([
  'add_to_cart',
  'remove_from_cart',
  'view_cart',
  'begin_checkout',
]);

function asEntry(value: unknown): Entry | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Entry;
}

function ecommerceOf(entry: Entry): Entry | null {
  return asEntry(entry.ecommerce);
}

function itemsOf(eco: Entry): unknown[] {
  if (Array.isArray(eco.items)) return eco.items;
  const detail = asEntry(eco.detail);
  if (detail && Array.isArray(detail.products)) return detail.products;
  return [];
}

function sumQuantities(items: unknown[]): number | undefined {
  if (items.length === 0) return undefined;
  let total = 0;
  let anyQty = false;
  for (const item of items) {
    const row = asEntry(item);
    if (!row) continue;
    const qty = parseItemQuantity(row.quantity ?? row.qty ?? row.item_quantity);
    if (qty != null) {
      total += qty;
      anyQty = true;
    }
  }
  if (anyQty) return total;
  return items.length;
}

/**
 * Collect cart aggregates from GA4 / UA ecommerce pushes in `dataLayer`.
 * Uses the latest cart-related event after the last `ecommerce: null` reset.
 */
export function collectGtmCartPartial(
  layer: unknown[] | undefined = typeof window !== 'undefined'
    ? window.dataLayer
    : undefined,
): CartPartial | null {
  if (!Array.isArray(layer) || layer.length === 0) return null;

  let lastReset = -1;
  for (let i = 0; i < layer.length; i += 1) {
    const entry = asEntry(layer[i]);
    if (entry && entry.ecommerce === null) lastReset = i;
  }

  let best: CartPartial | null = null;

  for (let i = lastReset + 1; i < layer.length; i += 1) {
    const entry = asEntry(layer[i]);
    if (!entry) continue;
    const event = textValue(entry.event)?.toLowerCase();
    if (!event || !CART_EVENTS.has(event)) continue;

    const eco = ecommerceOf(entry);
    if (!eco) continue;

    const items = itemsOf(eco);
    const itemsCount = sumQuantities(items);
    const cartValue = parseMoney(eco.value ?? eco.total ?? entry.value);
    const currency = normalizeCurrency(
      eco.currency ?? eco.currencyCode ?? entry.currency,
    );

    const emptyExplicit =
      (itemsCount === 0 && cartValue === 0) ||
      (event === 'view_cart' &&
        items.length === 0 &&
        (cartValue === 0 || cartValue === undefined));

    if (
      itemsCount == null &&
      cartValue == null &&
      currency == null &&
      !emptyExplicit
    ) {
      continue;
    }

    best = {
      source: CART_SOURCE.gtm,
      confidence: 'high',
      ...(itemsCount != null ? { itemsCount } : {}),
      ...(cartValue != null ? { cartValue } : {}),
      ...(currency ? { currency } : {}),
      ...(emptyExplicit ? { emptyExplicit: true, itemsCount: 0, cartValue: 0 } : {}),
    };
  }

  return best;
}
